import React, { useState } from "react";

/**
 * SmartImage — drop-in <img> replacement with:
 *  - Lazy loading (browser-native loading="lazy")
 *  - Automatic retry on failure (2x with backoff — for cold-start storage)
 *  - Soft fade-in once loaded
 *
 * Same API as <img>. Pass src, alt, className, etc.
 *
 * If src can change dynamically, pass key={src} from the parent so the
 * component remounts cleanly (avoids state-mismatch on src swap).
 */
function SmartImage({ src, alt = "", className = "", retries = 2, ...rest }) {
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const handleError = () => {
    if (attempt < retries) {
      const delay = attempt === 0 ? 600 : 1500;
      setTimeout(() => setAttempt((a) => a + 1), delay);
    }
  };

  // Cache-bust on retry so browser re-fetches instead of using the failed cached response
  const finalSrc = !src
    ? src
    : attempt === 0
      ? src
      : `${src}${src.includes("?") ? "&" : "?"}_r=${attempt}`;

  return (
    <img
      src={finalSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={handleError}
      className={`${className} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
      {...rest}
    />
  );
}

export default SmartImage;
